import React, { useMemo, useState, useRef, useEffect } from "react";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiAirplaneTakeoff, mdiCalendarRemove, mdiMedicalBag, mdiPlus, mdiCheckCircle } from "@mdi/js";
import { toast } from "react-toastify";
import ManageBookingsHeader from "./ManageBookingsHeader";
import BookingMiniCard from "./BookingMiniCard";
import ManualBookingModal from "./ManualBookingModal";
import { bookingApi } from "../api/booking";
import "./BookingWeekView.css";

const PIXELS_PER_HOUR = 45;
const GAP_SIZE = 8;

// Replicated from BookingDayView — same timeline math
const timeToMinutes = (timeString, firstHour) => {
    const [hourValue, minuteValue] = timeString.split(":").map(Number);
    return (hourValue - firstHour) * 60 + minuteValue;
};

const calculateHeight = durationMinutes => {
    const fullHeight = durationMinutes * (PIXELS_PER_HOUR / 60);
    return Math.max(fullHeight - GAP_SIZE, 28);
};

const calculatePosition = (startTime, durationMinutes, firstHour) => {
    const startMinutes = timeToMinutes(startTime, firstHour);
    const startPos = startMinutes * (PIXELS_PER_HOUR / 60);
    const durationMidpoint = (durationMinutes * (PIXELS_PER_HOUR / 60)) / 2;
    const cardHeight = calculateHeight(durationMinutes);
    const cardCenterOffset = cardHeight / 2;
    return startPos + durationMidpoint - cardCenterOffset;
};

const formatHour = hour => {
    if (hour === 0) return "12am";
    if (hour === 12) return "12pm";
    return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};

/**
 * BookingWeekView — 7-column week timeline showing all bookings for Mon–Sun.
 * Triggered when the calendar sidebar is collapsed in InstructorDashboard.
 */
const BookingWeekView = ({
    selectedDate,
    bookings = [],
    availabilitySlots = [],
    travelPeriods = [],
    blockedDates = [],
    sickDays = [],
    instructorProfile,
    instructorOrgId,
    currentUserId,
    onConfirmBooking,
    onRejectBooking,
    onMessageBooking,
    onSelectBooking,   // opens the SINGLE shared BookingDetailModal owned by InstructorDashboard
    onUpdateTime,
    onCancel,
    onSelectDate,
    onRefresh,
    // Header props (same as BookingDayView)
    onAddManualBooking,
    onConfirmAll,
    onOpenMessages,
    onSickDay,
    unreadMessageCount = 0,
    onMessagesRead,
    visibleDays = 7,           // NEW — defaults to 7 for backwards compat
    calendarCollapsed = false,
    onToggleCalendar,
    windowStart,               // Luxon DateTime — earliest loaded day (nav floor)
    windowEnd,                 // Luxon DateTime — latest loaded day (nav ceiling)
    onCrossWindow,             // (direction: "back"|"forward") => void — page month + load next chunk at window edge
    onGoToToday,               // () => void — reset BOTH week + month views to today
}) => {
    const [showManualBooking, setShowManualBooking] = useState(false);
    const [manualBookingSlot, setManualBookingSlot] = useState(null);

    const handleManualBookingSaved = async (bookingData) => {
        try {
            const booking = await bookingApi.createBooking(bookingData);
            toast.success('Booking created successfully!');
            // Do NOT refresh here: onRefresh (loadDashboardData) flips isLoading,
            // which unmounts this view and would close the modal before the
            // confirmation step shows. The refresh happens on modal close instead.
            return booking; // forwarded so ManualBookingModal can show the confirmation/credit step
        } catch (error) {
            console.error('Error creating manual booking:', error);
            toast.error(error.message || 'Failed to create manual booking.');
        }
    };

    const isTravelBooking = booking => {
        if (!travelPeriods || travelPeriods.length === 0) return false;
        const bookingDate = new Date(booking.bookingDate);
        return travelPeriods.some(period => {
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);
            return bookingDate >= startDate && bookingDate <= endDate;
        });
    };

    const BLOCK_TYPE_LABELS = { holiday: 'Public Holiday', leave: 'Annual Leave', personal: 'Personal Day' };

    const getBlockedContextForDay = day => {
        if (!blockedDates || blockedDates.length === 0) return null;
        return blockedDates.find(ctx => {
            try {
                const s = DateTime.fromISO(ctx.startDate).startOf('day');
                const e = DateTime.fromISO(ctx.endDate).startOf('day');
                const d = day.startOf('day');
                return d >= s && d <= e;
            } catch (_) { return null; }
        }) || null;
    };

    const getSickDayForDate = day => {
        if (!sickDays || sickDays.length === 0) return null;
        const dateStr = day.toISODate();
        return sickDays.find(s => s.date === dateStr) || null;
    };

    // Derive week start from selectedDate.
    // In split view (visibleDays < 7) start from the selected date itself so today's
    // days are shown rather than rewinding to the start of the week.
    const weekStart = useMemo(() => {
        if (visibleDays < 7) {
            if (!selectedDate) return DateTime.local().startOf("day");
            return selectedDate.startOf("day");
        }
        if (!selectedDate) return DateTime.local().startOf("week");
        return selectedDate.startOf("week");
    }, [selectedDate, visibleDays]);

    // Build array of visibleDays day objects for the week
    const weekDays = useMemo(() => {
        return Array.from({ length: visibleDays }, (_, i) => weekStart.plus({ days: i }));
    }, [weekStart, visibleDays]);

    // Navigate forward/back. In split view advance by visibleDays; in full week view by 7.
    // Nav bounds — the loaded data window (viewed month padded back a few weeks, +1
    // forward) is passed from the dashboard. Stepping a week would land us OUTSIDE
    // that window (which has no data fetched yet); when that happens we don't dead-end
    // — we call onCrossWindow, which pages the month calendar one month in that
    // direction (loading the next chunk on demand) and lands the week on the adjacent
    // week. So the arrows are always live and week + month stay in sync.
    const handlePrevWeek = () => {
        if (!onSelectDate) return;
        const target = visibleDays < 7
            ? weekStart.minus({ days: visibleDays })
            : weekStart.minus({ weeks: 1 }).plus({ days: 3 });
        // Would this step land before the loaded window? Hand off to the dashboard.
        if (windowStart && target.startOf("day") < windowStart.startOf("day")) {
            if (onCrossWindow) onCrossWindow("back");
            return;
        }
        onSelectDate(target);
    };
    const handleNextWeek = () => {
        if (!onSelectDate) return;
        const target = visibleDays < 7
            ? weekStart.plus({ days: visibleDays })
            : weekStart.plus({ weeks: 1 }).plus({ days: 3 });
        const targetLastDay = target.plus({ days: visibleDays - 1 });
        // Would this step land after the loaded window? Hand off to the dashboard.
        if (windowEnd && targetLastDay.startOf("day") > windowEnd.startOf("day")) {
            if (onCrossWindow) onCrossWindow("forward");
            return;
        }
        onSelectDate(target);
    };

    // Bookings for each day of the week (excluding cancelled)
    const weekBookings = useMemo(() => {
        const map = {};
        weekDays.forEach(day => {
            const dateStr = day.toISODate();
            map[dateStr] = bookings.filter(b => {
                return b.bookingDate.split("T")[0] === dateStr && b.status !== "cancelled";
            });
        });
        return map;
    }, [bookings, weekDays]);

    // Calculate timeline hours spanning availability slots + bookings this week
    const hours = useMemo(() => {
        let start = 9;
        let end = 17;

        // Expand to cover all availability slots for this week
        const weekDateStrs = new Set(weekDays.map(d => d.toISODate()));
        availabilitySlots.forEach(slot => {
            if (!weekDateStrs.has(slot.date)) return;
            if (!slot.startTime || !slot.endTime) return;
            try {
                start = Math.min(start, parseInt(slot.startTime.split(":")[0], 10));
                end = Math.max(end, parseInt(slot.endTime.split(":")[0], 10));
            } catch (_) {}
        });

        // Expand to cover any booking times this week
        Object.values(weekBookings).forEach(dayBkgs => {
            dayBkgs.forEach(b => {
                if (!b.timeSlot?.startTime || !b.timeSlot?.endTime) return;
                try {
                    start = Math.min(start, parseInt(b.timeSlot.startTime.split(":")[0], 10));
                    end = Math.max(end, parseInt(b.timeSlot.endTime.split(":")[0], 10));
                } catch (_) {}
            });
        });

        if (end - start < 4) end = start + 4;
        start = Math.max(0, start);
        end = Math.min(23, end);

        const arr = [];
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    }, [availabilitySlots, weekBookings, weekDays]);

    const firstHour = hours.length > 0 ? hours[0] : 9;
    const containerHeight = hours.length * PIXELS_PER_HOUR;

    const scrollBodyRef = useRef(null);

    useEffect(() => {
        const now = DateTime.local();
        const hour = now.hour + now.minute / 60;
        const scrollTop = Math.max(0, (hour - firstHour - 1) * PIXELS_PER_HOUR - 50);
        if (scrollBodyRef.current) scrollBodyRef.current.scrollTop = scrollTop;
    }, []); // mount only — intentional empty dep array
    const today = DateTime.local().toISODate();

    const getBookingStatusClass = status => {
        if (status === "confirmed") return "bwv-card--confirmed";
        if (status === "pending") return "bwv-card--pending";
        return "";
    };

    // Total unread across all bookings (same as BookingDayView)
    const totalUnreadCount = useMemo(() => {
        return bookings.reduce((total, b) => total + (b.unreadCount || 0), 0);
    }, [bookings]);

    return (
        <div className="booking-week-view">
            {/* Manage Bookings Header — same controls as day view */}
            <ManageBookingsHeader
                selectedDate={selectedDate}
                // Upcoming pendings only — must match the dashboard's confirm-all scope
                // (past-dated pendings are stale and excluded from the bulk confirm).
                pendingCount={bookings.filter(
                    b => b.status === "pending" && b.bookingDate.split("T")[0] >= DateTime.now().toISODate()
                ).length}
                totalBookingsCount={bookings.filter(b => b.status !== "cancelled").length}
                monthBookingPercentage={0}
                unreadMessageCount={unreadMessageCount || totalUnreadCount}
                onAddManualBooking={() => setShowManualBooking(true)}
                onConfirmAll={onConfirmAll}
                onOpenMessages={onOpenMessages}
                onGoToToday={onGoToToday || (onSelectDate ? () => onSelectDate(DateTime.now()) : undefined)}
                onRefresh={onRefresh}
                onSickDay={onSickDay}
                calendarCollapsed={calendarCollapsed}
                onToggleCalendar={onToggleCalendar}
            />

            {/* Week navigation header */}
            <div className="bwv-week-nav">
                <button
                    className="bwv-nav-btn"
                    onClick={handlePrevWeek}
                    aria-label="Previous week"
                    title="Previous week"
                >
                    <Icon path={mdiChevronLeft} size={1} />
                </button>
                <div className="bwv-week-pills">
                    {travelPeriods.filter(period => {
                        try {
                            const s = DateTime.fromISO(period.startDate).startOf("day");
                            const e = DateTime.fromISO(period.endDate).startOf("day");
                            return weekDays.some(d => d >= s && d <= e);
                        } catch (_) { return false; }
                    }).map((p, i) => (
                        <span key={`t-${i}`} className="bwv-travel-pill" style={{ "--period-color": p.color || "#d946ef" }}>
                            <Icon path={mdiAirplaneTakeoff} size={0.65} />
                            {p.location?.description || p.destination || "Travel"}
                        </span>
                    ))}
                </div>
                <h2 className="bwv-week-label">
                    {(() => {
                        const weekEnd = weekDays[weekDays.length - 1];
                        if (weekStart.month === weekEnd.month) {
                            return `${weekStart.toFormat("MMM d")} – ${weekEnd.toFormat("d, yyyy")}`;
                        }
                        return `${weekStart.toFormat("MMM d")} – ${weekEnd.toFormat("MMM d, yyyy")}`;
                    })()}
                </h2>
                <div className="bwv-week-pills" aria-hidden="true" />
                <button
                    className="bwv-nav-btn"
                    onClick={handleNextWeek}
                    aria-label="Next week"
                    title="Next week"
                >
                    <Icon path={mdiChevronRight} size={1} />
                </button>
            </div>

            {/* Grid: time ruler + 7 day columns */}
            <div className="bwv-grid-wrapper">
                {/* Day header row */}
                <div className="bwv-header-row" style={{ gridTemplateColumns: `44px repeat(${visibleDays}, 1fr)` }}>
                    <div className="bwv-time-gutter" /> {/* spacer for time ruler */}
                    {weekDays.map(day => {
                        const isToday = day.toISODate() === today;
                        const isSelected = selectedDate && day.toISODate() === selectedDate.toISODate();
                        return (
                            <div
                                key={day.toISODate()}
                                className={`bwv-day-header ${isToday ? "bwv-day-header--today" : ""} ${isSelected ? "bwv-day-header--selected" : ""}`}
                                onClick={() => onSelectDate && onSelectDate(day)}
                                title="Click to view day">
                                <span className="bwv-day-name">{day.toFormat("EEE")}</span>
                                <span className={`bwv-day-num ${isToday ? "bwv-day-num--today" : ""}`}>{day.toFormat("d")}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Timeline body */}
                <div className="bwv-body" ref={scrollBodyRef} style={{ gridTemplateColumns: `44px repeat(${visibleDays}, 1fr)` }}>
                    {/* Time ruler */}
                    <div className="bwv-time-ruler">
                        {hours.map(hour => (
                            <div key={hour} className="bwv-time-marker">
                                {formatHour(hour)}
                            </div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {weekDays.map(day => {
                        const dateStr = day.toISODate();
                        const isToday = dateStr === today;
                        const isSelected = selectedDate && dateStr === selectedDate.toISODate();
                        const dayBkgs = weekBookings[dateStr] || [];

                        // Availability slots for this day (home or travel, same logic as BookingDayView)
                        const activeTravelPeriod = travelPeriods.find(period => {
                            try {
                                const s = DateTime.fromISO(period.startDate).startOf("day");
                                const e = DateTime.fromISO(period.endDate).startOf("day");
                                const d = day.startOf("day");
                                return d >= s && d <= e;
                            } catch (_) {
                                return false;
                            }
                        });
                        const activeBlockedCtx = getBlockedContextForDay(day);
                        const activeSickDay = !activeBlockedCtx && !activeTravelPeriod ? getSickDayForDate(day) : null;
                        const daySlots = availabilitySlots.filter(slot => {
                            if (slot.date !== dateStr) return false;
                            if (activeTravelPeriod) return slot.location?.type === "travel";
                            return slot.location?.type !== "travel";
                        });
                        // Hide any available slot that OVERLAPS a non-cancelled booking — not
                        // just one whose start time matches. A 2-hour booking (13:00–15:00) must
                        // suppress both the 13:00 and 14:00 hourly slots. Exact-start matching left
                        // the inner slot visible behind the booking card. Overlap also covers
                        // `completed` bookings, which the backend does not pre-mark as booked.
                        const overlapsBooking = slot => dayBkgs.some(b => {
                            const bStart = b.timeSlot?.startTime;
                            const bEnd = b.timeSlot?.endTime;
                            if (!bStart || !bEnd || !slot.startTime || !slot.endTime) return false;
                            return slot.startTime < bEnd && slot.endTime > bStart;
                        });
                        // Never offer bookable availability on a past day — a clickable
                        // "Available" card there could create a manual booking in the past
                        // by accident. Past days show existing bookings only.
                        const isPastDay = dateStr < today;
                        const availableSlots = isPastDay
                            ? []
                            : daySlots.filter(s => s.status === "available" && !overlapsBooking(s));

                        return (
                            <div
                                key={dateStr}
                                className={`bwv-day-col ${isToday ? "bwv-day-col--today" : ""} ${isSelected ? "bwv-day-col--selected" : ""} ${activeTravelPeriod && !activeBlockedCtx ? "bwv-day-col--travel" : ""} ${activeBlockedCtx ? "bwv-day-col--blocked" : ""} ${activeSickDay ? "bwv-day-col--sick" : ""} ${isPastDay ? "bwv-day-col--past" : ""}`}
                                style={{
                                    minHeight: containerHeight,
                                    ...(activeTravelPeriod && !activeBlockedCtx ? { "--period-color": activeTravelPeriod.color || "#d946ef" } : {}),
                                    ...(activeBlockedCtx ? { "--period-color": activeBlockedCtx.color || "#999999" } : {})
                                }}>

                                {/* Hour grid lines */}
                                {hours.map(hour => (
                                    <div key={hour} className="bwv-hour-line" style={{ top: (hour - firstHour) * PIXELS_PER_HOUR }} />
                                ))}

                                {/* Blocked date overlay */}
                                {activeBlockedCtx && (
                                    <div
                                        className="bwv-block-col-overlay"
                                        style={{ "--period-color": activeBlockedCtx.color || "#999999" }}
                                        title={`${BLOCK_TYPE_LABELS[activeBlockedCtx.context] || activeBlockedCtx.context}: ${activeBlockedCtx.reason}`}
                                    >
                                        <Icon path={mdiCalendarRemove} size={0.85} />
                                        <span>{BLOCK_TYPE_LABELS[activeBlockedCtx.context] || activeBlockedCtx.context}</span>
                                        {activeBlockedCtx.reason && (
                                            <span className="bwv-block-col-overlay-reason">{activeBlockedCtx.reason}</span>
                                        )}
                                    </div>
                                )}

                                {/* Sick day overlay */}
                                {activeSickDay && (
                                    <div
                                        className="bwv-sick-col-overlay"
                                        title={`Sick Day${activeSickDay.reason ? ': ' + activeSickDay.reason : ''}`}
                                    >
                                        <Icon path={mdiMedicalBag} size={0.85} />
                                        <span>Sick Day</span>
                                        {activeSickDay.reason && (
                                            <span className="bwv-sick-col-overlay-reason">{activeSickDay.reason}</span>
                                        )}
                                    </div>
                                )}

                                {/* Travel day overlay — only on BLANK travel days (no bookings,
                                    no available slots). Mirrors the blocked/sick overlay: a plane
                                    icon + destination label, so the leave/return days (and any
                                    empty travel day, including past ones once slots are gone) read
                                    as "travelling" rather than an empty column. Days WITH bookings
                                    or slots show those instead — don't override them. */}
                                {activeTravelPeriod && !activeBlockedCtx && !activeSickDay &&
                                 dayBkgs.length === 0 && availableSlots.length === 0 && (
                                    <div
                                        className="bwv-travel-col-overlay"
                                        style={{ "--period-color": activeTravelPeriod.color || "#d946ef" }}
                                        title={`Travel: ${activeTravelPeriod.location?.description || activeTravelPeriod.destination || "Travel"}`}
                                    >
                                        <Icon path={mdiAirplaneTakeoff} size={0.85} />
                                        <span>{activeTravelPeriod.location?.description || activeTravelPeriod.destination || "Travel"}</span>
                                    </div>
                                )}

                                {/* Availability cards — same style as day view */}
                                {availableSlots.map((slot, idx) => {
                                    if (!slot.startTime || !slot.endTime) return null;
                                    const [sh, sm] = slot.startTime.split(":").map(Number);
                                    const [eh, em] = slot.endTime.split(":").map(Number);
                                    const duration = (eh * 60 + em) - (sh * 60 + sm);
                                    if (duration <= 0) return null;
                                    const topPx = calculatePosition(slot.startTime, duration, firstHour);
                                    const heightPx = calculateHeight(duration);
                                    return (
                                        <div
                                            key={idx}
                                            className={`floating-card available-card ${activeTravelPeriod ? "available-card--travel" : ""}`}
                                            style={{ top: topPx, height: heightPx }}
                                            onClick={() => {
                                                setManualBookingSlot({
                                                    date: slot.date,
                                                    startTime: slot.startTime,
                                                    endTime: slot.endTime,
                                                });
                                                setShowManualBooking(true);
                                            }}
                                        >
                                            <span className="available-label">Available</span>
                                            <Icon path={mdiPlus} size={0.55} className="available-add-icon" />
                                        </div>
                                    );
                                })}

                                {/* Booking cards */}
                                {dayBkgs.map(booking => {
                                    if (!booking.timeSlot?.startTime) return null;
                                    const duration = (() => {
                                        try {
                                            const [sh, sm] = booking.timeSlot.startTime.split(":").map(Number);
                                            const [eh, em] = booking.timeSlot.endTime.split(":").map(Number);
                                            return (eh * 60 + em) - (sh * 60 + sm);
                                        } catch (_) {
                                            return 60;
                                        }
                                    })();
                                    const topPx = calculatePosition(booking.timeSlot.startTime, duration, firstHour);
                                    const heightPx = calculateHeight(duration);

                                    // Past days recede visually: confirmed/completed → muted grey
                                    // + done badge; pending → desaturated yellow (still a visible
                                    // loose end, but clearly not a live booking). The day boundary
                                    // is the viewer's local today; the backend flips status to
                                    // "completed" authoritatively in the venue's timezone.
                                    // isPastDay computed once at the day level above.
                                    const isPending = booking.status === "pending";
                                    const statusClass = isPending ? "pending" : "confirmed";

                                    return (
                                        <div
                                            key={booking.id}
                                            className={`floating-card booking-card ${statusClass}${isPastDay ? " past" : ""}`}
                                            style={{ top: topPx, height: heightPx }}>
                                            {isPastDay && !isPending && (
                                                <Icon path={mdiCheckCircle} size={0.55} className="bwv-completed-badge" />
                                            )}
                                            <BookingMiniCard
                                                booking={booking}
                                                onEdit={() => onSelectBooking(booking)}
                                                compact={true}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Manual booking modal */}
            {showManualBooking && (
                <ManualBookingModal
                    isOpen={showManualBooking}
                    selectedDate={manualBookingSlot ? DateTime.fromISO(manualBookingSlot.date) : selectedDate}
                    initialTime={manualBookingSlot?.startTime ?? null}
                    initialEndTime={manualBookingSlot?.endTime ?? null}
                    instructorProfile={instructorProfile}
                    instructorOrgId={instructorOrgId}
                    existingBookings={bookings.filter(b => b.status !== 'cancelled')}
                    travelPeriods={travelPeriods}
                    onClose={() => { setShowManualBooking(false); setManualBookingSlot(null); onRefresh?.(); }}
                    onSave={handleManualBookingSaved}
                />
            )}

            {/* Booking detail modal (reuses existing component) */}
        </div>
    );
};

export default BookingWeekView;
